import { Fragment, useMemo, type ReactNode } from 'react';
import { formatCopy, namespaceCopyFor, useNamespaceCopy } from '@/i18n/ns';
import { useAppLocale } from '@/i18n/context';
import workspaceInterfaceCopy from '@/i18n/ns/workspace-interface';

type Message = keyof typeof workspaceInterfaceCopy.en;
type Values = Record<string, string | number>;
export type WorkspaceText = ((message: Message, values?: Values) => string) & {
  readonly locale: string;
};

export function workspaceTextFor(locale: string): WorkspaceText {
  const copy = namespaceCopyFor(workspaceInterfaceCopy, locale);
  return Object.assign(
    (message: Message, values: Values = {}) => formatCopy(copy[message], values),
    { locale },
  );
}

/** Authored interface messages only; names, documents and model answers remain values. */
export function useWorkspaceText() {
  const copy = useNamespaceCopy(workspaceInterfaceCopy);
  const locale = useAppLocale();
  return useMemo(
    () => ({
      locale,
      text: workspaceTextFor(locale),
      rich: (message: Message, values: Record<string, ReactNode> = {}) =>
        copy[message].split(/(\{[^}]+\})/g).map((part, index) => {
          const key = part.slice(1, -1);
          return <Fragment key={index}>{Object.hasOwn(values, key) ? values[key] : part}</Fragment>;
        }),
    }),
    [copy, locale],
  );
}
